import React, { useEffect, useMemo, useRef, useState } from "react";
import { toCategoryOptions } from "../categoryFilter";
import { CRAFT_LEVELS, CraftLevel, Option, ProductListFilters, setCraftLevel } from "./productListFilters";
import { PlIcon } from "./plIcons";

const LEVEL_LABELS: Record<CraftLevel, string> = {
  artform: "Artform",
  type: "Type",
  category: "Category",
  subCategory: "Sub-category",
  weaveType: "Weave type",
};

/** Master-data loaders (the redux actions the old filter bar used). */
export type CraftLoaders = {
  getArtforms: (params: any, cb: (res: any) => void) => void;
  getTypes: (params: any, cb: (res: any) => void) => void;
  getCategory: (params: any, cb: (res: any) => void) => void;
  getSubCategorys: (params: any, cb: (res: any) => void) => void;
  getWeaveType: (params: any, cb: (res: any) => void) => void;
};

const idOf = (value: any) => (value && typeof value === "object" && value.value && !value.byName ? String(value.value) : undefined);

const isPicked = (filters: ProductListFilters, level: CraftLevel) => {
  const value: any = filters[level];
  if (!value || !value.value) return false;
  return !(level === "category" && value.byName);
};

const docsToOptions = (docs: any): Option[] =>
  (Array.isArray(docs) ? docs : [])
    .filter((row: any) => row && row.name && row._id)
    .map((row: any) => ({ label: String(row.name), value: String(row._id) }));

/**
 * Artform → type → category → sub-category → weave, one level at a time. Each pick narrows
 * the next list (same endpoints and params as before), and picked levels show as removable chips.
 */
const CraftWeavePanel = ({
  filters,
  onChange,
  loaders,
}: {
  filters: ProductListFilters;
  onChange: (next: ProductListFilters) => void;
  loaders: CraftLoaders;
}) => {
  const level: CraftLevel | null = CRAFT_LEVELS.find((l) => !isPicked(filters, l)) || null;
  const [search, setSearch] = useState("");
  // Options are kept with the level they were loaded for, so a list from the previous level
  // is never shown (or picked) while the next level loads.
  const [loaded, setLoaded] = useState<{ level: CraftLevel | null; options: Option[] }>({ level: null, options: [] });
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const artformId = idOf(filters.artform);
  const typeId = idOf(filters.type);
  const categoryId = idOf(filters.category);
  const subCategoryId = idOf(filters.subCategory);

  useEffect(() => {
    setSearch("");
  }, [level]);

  useEffect(() => {
    if (!level) return undefined;
    const mine = ++seq.current;
    const term = search.trim() || undefined;
    const done = (list: Option[]) => {
      if (mine !== seq.current) return;
      setLoaded({ level, options: list });
      setLoading(false);
    };
    setLoading(true);
    const timer = setTimeout(() => {
      const base = { offset: 0, limit: 50 };
      if (level === "artform") loaders.getArtforms({ ...base, artformName: term }, (res) => done(docsToOptions(res?.data?.docs)));
      if (level === "type") loaders.getTypes({ ...base, artFormId: artformId, typeName: term }, (res) => done(docsToOptions(res?.data?.docs)));
      if (level === "category")
        loaders.getCategory({ ...base, typeId, artformId: typeId ? artformId : undefined, categoryName: term }, (res) =>
          done(toCategoryOptions(res?.data?.docs, false))
        );
      if (level === "subCategory")
        loaders.getSubCategorys({ ...base, categoryId, typeId, artformId, subCategoryName: term }, (res) =>
          done(docsToOptions(res?.data?.docs))
        );
      if (level === "weaveType")
        loaders.getWeaveType({ ...base, weaveTypeName: term, subCategoryId, categoryId, typeId, artformId }, (res) =>
          done(docsToOptions(res?.data?.docs))
        );
    }, term ? 300 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, search, artformId, typeId, categoryId, subCategoryId]);

  const options = loaded.level === level ? loaded.options : [];
  const picked = useMemo(() => CRAFT_LEVELS.filter((l) => isPicked(filters, l)), [filters]);
  const nameCategory: any = filters.category && (filters.category as any).byName ? filters.category : null;

  return (
    <div className="pl-craft">
      {picked.length || nameCategory ? (
        <div className="pl-chips is-wrap pl-craft__trail">
          {picked.map((l, i) => (
            <React.Fragment key={l}>
              {i > 0 ? <span className="pl-craft__sep" aria-hidden="true">›</span> : null}
              <button
                type="button"
                className="pl-chip is-on"
                aria-label={`Remove ${LEVEL_LABELS[l]} ${(filters[l] as any)?.label}`}
                onClick={() => onChange(setCraftLevel(filters, l, ""))}
              >
                {(filters[l] as any)?.label}
                <PlIcon name="x" size={14} />
              </button>
            </React.Fragment>
          ))}
          {nameCategory && !picked.includes("category") ? (
            <span className="pl-chip is-soft">Category: {nameCategory.label} (any type)</span>
          ) : null}
        </div>
      ) : null}
      {level ? (
        <>
          <p className="pl-note">Each level you pick narrows the next.</p>
          <label className="pl-input pl-input--search">
            <PlIcon name="search" size={18} />
            <input
              type="search"
              aria-label={`Search ${LEVEL_LABELS[level].toLowerCase()}`}
              placeholder={`Search ${LEVEL_LABELS[level].toLowerCase()}…`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <div className="pl-sec">{LEVEL_LABELS[level]} · pick one</div>
          <div className="pl-craft__list" aria-busy={loading || undefined}>
            {!loading && options.length === 0 ? <p className="pl-note">Nothing found{search ? ` for “${search}”` : ""}.</p> : null}
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="pl-check"
                onClick={() => onChange(setCraftLevel(filters, level, { label: option.label, value: option.value }))}
              >
                <span className="pl-check__box" aria-hidden="true" />
                <span className="pl-check__text">{option.label}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="pl-note">All five levels are picked. Tap a chip to change one.</p>
      )}
    </div>
  );
};

export default CraftWeavePanel;
